<main id="main-content" class="ds-docs">
  <header class="docs-hero">
    <a routerLink="/" class="docs-back">IQLabTest product</a>
    <p class="ds-eyebrow">INTERNAL DESIGN SYSTEM · VERSION 1.0</p>
    <h1>Clear thinking, made visible.</h1>
    <p>
      Foundations, components, and assessment patterns for a calm, trustworthy cognitive assessment
      platform.
    </p>
    <a dsButton routerLink="/design-system/examples">Open reference screens</a>
  </header>

  <div class="docs-layout">
    <nav class="docs-nav" aria-label="Design system sections">
      <strong>Foundations</strong>
      <a [routerLink]="[]" fragment="principles">Principles</a>
      <a [routerLink]="[]" fragment="brand">Brand</a>
      <a [routerLink]="[]" fragment="color">Color</a>
      <a [routerLink]="[]" fragment="type">Typography</a>
      <a [routerLink]="[]" fragment="components">Components</a>
      <a [routerLink]="[]" fragment="assessment">Assessment UX</a>
      <a [routerLink]="[]" fragment="accessibility">Accessibility</a>
      <a [routerLink]="[]" fragment="responsive">Responsive</a>
    </nav>

    <div class="docs-content">
      <section id="principles">
        <p class="ds-eyebrow">FOUNDATIONS</p>
        <h2>Design principles</h2>
        <div class="principle-grid">
          @for (
            principle of [
              ['Clarity before decoration', 'Hierarchy and comprehension justify every detail.'],
              ['Calm under concentration', 'Assessment screens remove competing actions.'],
              ['Evidence over theatre', 'Results communicate uncertainty without judgment.'],
              ['Accessible by construction', 'Semantic, keyboard-ready patterns are the default.'],
            ];
            track principle[0]
          ) {
            <ds-card
              ><strong>{{ principle[0] }}</strong>
              <p>{{ principle[1] }}</p></ds-card
            >
          }
        </div>
      </section>

      <section id="brand">
        <p class="ds-eyebrow">IDENTITY</p>
        <h2>Brand</h2>
        <div class="brand-specimen">
          <span class="brand-mark" aria-hidden="true">IQ</span>
          <div><strong>IQLabTest</strong><span>Cognitive assessment, explained clearly</span></div>
        </div>
        <p class="reading-copy">
          The IQ monogram is compact, precise, and quiet. It appears once in the shell, always next
          to the full product name. Gradients, alternate marks, and decorative shadows are not part
          of the core product identity.
        </p>
      </section>

      <section id="color">
        <p class="ds-eyebrow">TOKENS</p>
        <h2>Semantic color</h2>
        <p class="reading-copy">
          Components use roles rather than palette values, keeping contrast and behavior consistent.
        </p>
        <div class="swatch-grid">
          @for (color of colors; track color.token) {
            <div class="swatch">
              <span [style.background]="'var(' + color.token + ')'" aria-hidden="true"></span
              ><strong>{{ color.name }}</strong
              ><code>{{ color.token }}</code>
            </div>
          }
        </div>
      </section>

      <section id="type">
        <p class="ds-eyebrow">TYPOGRAPHY</p>
        <h2>A multilingual, system-first type scale</h2>
        <div class="type-specimen">
          <p class="display-type">Intelligence, explained.</p>
          <p class="heading-type">A consistent heading establishes the next decision.</p>
          <p class="body-type">
            Body copy remains readable across scripts, screen sizes, and long assessment sessions.
            Editorial text is limited to a comfortable measure.
          </p>
          <p class="utility-type">UTILITY · STATUS · METADATA</p>
        </div>
      </section>

      <section id="components">
        <p class="ds-eyebrow">COMPONENT LIBRARY</p>
        <h2>Production controls and states</h2>
        <ds-card class="specimen">
          <h3>Buttons</h3>
          <div class="specimen-row">
            <button dsButton type="button">Primary action</button>
            <button dsButton variant="secondary" type="button">Secondary</button>
            <button dsButton variant="quiet" type="button">Quiet</button>
            <button dsButton variant="danger" type="button">Destructive</button>
            <button dsButton type="button" disabled>Disabled</button>
          </div>
        </ds-card>

        <ds-card class="specimen">
          <h3>Forms</h3>
          <div class="form-grid">
            <ds-field label="Email address" forId="docs-email" hint="Used for account access."
              ><input dsInput id="docs-email" type="email" placeholder="name@example.com"
            /></ds-field>
            <ds-field label="Assessment language" forId="docs-language"
              ><select dsSelect id="docs-language">
                <option>English</option>
                <option>Nederlands</option>
              </select></ds-field
            >
          </div>
          <div class="specimen-row">
            <label class="check-example"
              ><input dsCheckbox type="checkbox" /> Remember preference</label
            >
            <label class="check-example"
              ><input dsRadio type="radio" name="density" checked /> Comfortable</label
            >
            <label class="check-example"
              ><input dsRadio type="radio" name="density" /> Compact</label
            >
          </div>
        </ds-card>

        <ds-card class="specimen">
          <h3>Status and feedback</h3>
          <div class="specimen-row">
            <ds-badge>Draft</ds-badge><ds-badge tone="success">Complete</ds-badge
            ><ds-badge tone="warning">Review</ds-badge><ds-badge tone="danger">Failed</ds-badge>
          </div>
          <ds-alert title="Changes saved"
            ><p>The assessment configuration is up to date.</p></ds-alert
          >
          <ds-progress [value]="64" label="Campaign completion" />
        </ds-card>

        <ds-card class="specimen">
          <h3>Navigation and overlays</h3>
          <ds-tabs
            [tabs]="tabs"
            [selectedId]="selectedTab()"
            (selectedIdChange)="selectedTab.set($event)"
          />
          <p>
            Selected section: <strong>{{ selectedTab() }}</strong>
          </p>
          <button dsButton variant="secondary" type="button" (click)="openDialog()">
            Open dialog
          </button>
          <ds-dialog #exampleDialog title="Confirm this action" eyebrow="Dialog example"
            ><p>Native modal behavior manages focus, Escape, and focus restoration.</p>
            <div dsDialogActions>
              <button dsButton type="button" (click)="exampleDialog.close()">Done</button>
            </div></ds-dialog
          >
        </ds-card>

        <ds-card class="specimen">
          <h3>Data and states</h3>
          <ds-table label="Recent assessments"
            ><thead>
              <tr>
                <th>Candidate</th>
                <th>Status</th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Alex Morgan</td>
                <td><ds-badge tone="success">Complete</ds-badge></td>
                <td>118</td>
              </tr>
              <tr>
                <td>Sam Rivera</td>
                <td><ds-badge tone="warning">In progress</ds-badge></td>
                <td>—</td>
              </tr>
            </tbody></ds-table
          >
          <ds-state
            kind="empty"
            title="No archived assessments"
            message="Completed assessments will appear here."
          />
        </ds-card>
      </section>

      <section id="assessment">
        <p class="ds-eyebrow">ASSESSMENT UX</p>
        <h2>Stable, focused question pattern</h2>
        <div class="assessment-toolbar">
          <ds-assessment-progress [answered]="11" [total]="40" /><ds-assessment-timer
            [seconds]="1482"
          />
        </div>
        <ds-question-card
          domain="Pattern reasoning"
          [position]="12"
          [total]="40"
          prompt="Which option completes the sequence?"
        >
          <ds-answer-option optionKey="A" label="Option one" />
          <ds-answer-option optionKey="B" label="Option two" [selected]="true" />
          <ds-answer-option optionKey="C" label="Option three" />
          <ds-answer-option optionKey="D" label="Option four" />
        </ds-question-card>
        <ds-question-navigator [position]="12" [total]="40" />
        <div class="results-specimen">
          <ds-score-visualization
            [value]="74"
            displayValue="118"
            label="Indicative estimate"
          /><ds-result-card
            title="Above average range"
            score="118"
            range="112–124"
            summary="Interpret the estimate with its range and testing context."
          />
        </div>
      </section>

      <section id="accessibility">
        <p class="ds-eyebrow">WCAG 2.2 AA</p>
        <h2>Accessibility is a component contract</h2>
        <ul class="reading-copy">
          <li>Visible, high-contrast focus on every interactive control.</li>
          <li>Native semantics before custom ARIA.</li>
          <li>44px minimum touch targets and readable multilingual text.</li>
          <li>Reduced motion and forced-colors support.</li>
          <li>Errors connected to fields and announced at the right time.</li>
        </ul>
      </section>

      <section id="responsive">
        <p class="ds-eyebrow">RESPONSIVE SYSTEM</p>
        <h2>Content-driven, not device-driven</h2>
        <p class="reading-copy">
          Layouts progress from a single touch-friendly column to tablet and desktop grids at 480,
          768, 1024, and 1280 CSS pixels. Assessment screens additionally respond to short viewport
          height so prompts, answers, and navigation remain visible together.
        </p>
      </section>
    </div>
  </div>
</main>
